import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button, Input, Select, Table, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Box } from 'atoms';
import { AntdTable } from 'components/custom-table/Style';
import StatusChip from 'componentsv2/common/StatusChip';
import { numberWithCommas } from 'utils/NumberCommaSeprator';
import { deleteWarehouseFixedCostV2, updateWarehouseFixedCostV2 } from 'store/warehousingSliceV2';
import { ADD_COST_HEAD_OPTION, COST_HEAD_OPTIONS } from '../warehousing.constants';
import FixedCostsHeaderActions from './FixedCostsHeaderActions';
import {
	AddRowNote,
	CostHeadAddRow,
	EmptyState,
	MutedNote,
	NumCell,
	Panel,
	PanelHeader,
	PanelSubtitle,
	PanelTitle,
	QuickAddField,
} from './warehousingStyles';

const money = (value) =>
	value === null || value === undefined ? '—' : `₹ ${numberWithCommas(Math.round(value))}`;

/** cost_head_code is the stable identity across versions — derive it from the name. */
const toCode = (name) =>
	name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, 60);

/**
 * Why a line item cannot be staged yet, or null when it can.
 *
 * The head and value are required; a reason is not, since adding a head does not change
 * anything the project has already reported. The Add button stays disabled until both are
 * present, so this only ever fires on the Enter-key path.
 */
const addLineItemError = ({ name, code, value, costToCustomer, addingCustom, taken }) => {
	if (!name) {
		return addingCustom ? 'Enter a name for the new cost head.' : 'Select a cost head.';
	}
	if (value === '' || Number.isNaN(Number(value))) return 'Enter a value for the cost head.';
	if (!code) return 'Cost head name must contain letters or numbers.';
	if (taken) return `"${name}" is already listed.`;
	if (costToCustomer !== '' && Number(costToCustomer) < Number(value)) {
		return 'Cost to Customer cannot be less than the cost head value.';
	}
	return null;
};

const generatedRevenue = (value, costToCustomer) =>
	costToCustomer === null || costToCustomer === undefined
		? null
		: Number(costToCustomer) - Number(value || 0);

const costToCustomerCellContent = (editing, setRowCostToCustomer, value, record) =>
	editing ? (
		<Input
			type="number"
			min={0}
			size="small"
			placeholder="Same as Value"
			value={value}
			disabled={record.removed}
			aria-label={`Cost to Customer for ${record.cost_head_name}`}
			onChange={(e) => setRowCostToCustomer(record.cost_head_code, e.target.value)}
		/>
	) : (
		<NumCell>{value === '' || value === null ? '—' : money(value)}</NumCell>
	);

/** A row with no cost_to_customer staged reads as equal to its Value (0 Generated
 * Revenue) — same convention the backend applies when aggregating MTD totals. */
const rowCostToCustomer = (r) =>
	r.cost_to_customer === '' || r.cost_to_customer === null || r.cost_to_customer === undefined
		? Number(r.value) || 0
		: Number(r.cost_to_customer);

const sumCostToCustomer = (rows) =>
	rows.reduce((sum, r) => (r.removed ? sum : sum + rowCostToCustomer(r)), 0);

/** Run one async step per item, in order — a failure stops the run rather than
 * scattering partial writes across an already-in-flight Promise.all. */
const runSequentially = (items, step) =>
	items.reduce((chain, item) => chain.then(() => step(item)), Promise.resolve());

const summaryCells = ({
	editing,
	isCostPlus,
	total,
	totalCostToCustomer,
	totalGeneratedRevenue,
}) => [
	<strong key="label">Total</strong>,
	<NumCell key="value" style={{ textAlign: editing ? 'left' : 'right' }}>
		<strong>{money(total)}</strong>
	</NumCell>,
	...(isCostPlus
		? [
				<NumCell key="ctcu">
					<strong>{money(totalCostToCustomer)}</strong>
				</NumCell>,
				<NumCell key="generated_revenue">
					<strong>{money(totalGeneratedRevenue)}</strong>
				</NumCell>,
		  ]
		: []),
	...(editing ? [null, null] : []),
];

/** A staged row differs from what Save would actually change nothing about. */
const isRowDirty = (r) =>
	r.removed ||
	r.isNew ||
	String(r.value) !== String(r.original ?? '') ||
	String(r.cost_to_customer) !== String(r.originalCostToCustomer ?? '');

/** The one reason Save can't run yet, or null when the staged rows are all valid. */
const validateFixedCostSave = (removals, dirty) => {
	const invalid = dirty.find((r) => r.value === '' || Number(r.value) < 0);
	if (invalid) return `Enter a valid value for "${invalid.cost_head_name}".`;

	const invalidCtcu = dirty.find(
		(r) => r.cost_to_customer !== '' && Number(r.cost_to_customer) < Number(r.value)
	);
	if (invalidCtcu) {
		return `Cost to Customer for "${invalidCtcu.cost_head_name}" cannot be less than its Value.`;
	}

	// A newly added head needs no reason; re-valuing or removing an existing one does.
	const missingReason = [...removals, ...dirty].find((r) => !r.isNew && !(r.reason || '').trim());
	if (missingReason) return `Enter a reason for "${missingReason.cost_head_name}".`;

	return null;
};

const generatedRevenueCellContent = (record) => {
	const gen = generatedRevenue(
		record.value,
		record.cost_to_customer === '' ? null : record.cost_to_customer
	);
	return <NumCell>{gen === null ? '—' : money(gen)}</NumCell>;
};

/**
 * Cost Plus only: the client-billing basis for a head, and what it earns in Generated
 * Revenue on top of Value. Split out of buildColumns so its render callbacks' own
 * branching is not counted against buildColumns' cognitive complexity.
 */
const buildCostPlusColumns = (editing, setRowCostToCustomer) => [
	{
		title: 'Cost to Customer (₹)',
		dataIndex: 'cost_to_customer',
		key: 'cost_to_customer',
		align: editing ? 'left' : 'right',
		width: editing ? 200 : undefined,
		render: (value, record) =>
			costToCustomerCellContent(editing, setRowCostToCustomer, value, record),
	},
	{
		title: 'Generated Revenue',
		key: 'generated_revenue',
		align: 'right',
		render: (_, record) => generatedRevenueCellContent(record),
	},
];

/** Columns shown only while editing; the remove column additionally needs delete rights. */
const editOnlyColumns = (canDelete, reasonColumn, removeColumn) =>
	canDelete ? [reasonColumn, removeColumn] : [reasonColumn];

/**
 * Column set for the panel. Built outside the component so the render functions — and
 * their per-row conditionals — do not count against its complexity, matching how the
 * repo keeps larger column configs beside their page.
 */
const buildColumns = ({
	editing,
	canDelete,
	isCostPlus,
	setRowValue,
	setRowCostToCustomer,
	setRowReason,
	removeRow,
	restoreRow,
}) => {
	const baseColumns = [
		{
			title: 'Cost Head',
			dataIndex: 'cost_head_name',
			key: 'cost_head_name',
			render: (value, record) => (
				<span
					style={
						record.removed
							? { textDecoration: 'line-through', color: '#bfbfbf' }
							: undefined
					}
				>
					{value || '—'}
					{record.isNew && (
						<>
							{' '}
							<StatusChip tone="primary" label="New" />
						</>
					)}
					{record.removed && (
						<>
							{' '}
							<StatusChip tone="danger" label="Removing" />
						</>
					)}
				</span>
			),
		},
		{
			title: 'Cost to Company (₹)',
			dataIndex: 'value',
			key: 'value',
			align: editing ? 'left' : 'right',
			width: editing ? 200 : undefined,
			render: (value, record) =>
				editing ? (
					<Input
						type="number"
						min={0}
						size="small"
						value={value}
						disabled={record.removed}
						aria-label={`Value for ${record.cost_head_name}`}
						onChange={(e) => setRowValue(record.cost_head_code, e.target.value)}
					/>
				) : (
					<NumCell>{money(value)}</NumCell>
				),
		},
	];

	// Hidden for a per_order_slab project, which never populates cost_to_customer.
	if (isCostPlus) {
		baseColumns.push(...buildCostPlusColumns(editing, setRowCostToCustomer));
	}

	// A change is not saveable without its own reason, so the field sits on the row it
	// explains rather than once at the bottom of the panel.
	const reasonColumn = {
		title: 'Reason',
		dataIndex: 'reason',
		key: 'reason',
		// A row staged for the first time has nothing to justify, so it shows no field —
		// only a change to an already-saved value (or its removal) asks for one.
		render: (value, record) =>
			record.isNew ? (
				<MutedNote>Not required when adding</MutedNote>
			) : (
				<Input
					size="small"
					value={value || ''}
					placeholder={
						record.removed ? 'Why is this being removed?' : 'Why is this changing?'
					}
					aria-label={`Reason for ${record.cost_head_name}`}
					onChange={(e) => setRowReason(record.cost_head_code, e.target.value)}
				/>
			),
	};

	// Only offered while editing — the panel is otherwise read-only
	const removeColumn = {
		title: '',
		key: 'remove',
		width: 56,
		align: 'center',
		render: (_, record) =>
			record.removed ? (
				<Button type="link" size="small" onClick={() => restoreRow(record.cost_head_code)}>
					Undo
				</Button>
			) : (
				<DeleteOutlined
					style={{ color: '#ff4d4f', cursor: 'pointer' }}
					aria-label={`Remove ${record.cost_head_name}`}
					onClick={() => removeRow(record.cost_head_code)}
				/>
			),
	};

	if (!editing) return baseColumns;
	// Removal is its own update type (delete_warehouse_fixed_cost), gated separately
	return [...baseColumns, ...editOnlyColumns(canDelete, reasonColumn, removeColumn)];
};

/**
 * Fixed cost heads, read-only until Edit is pressed.
 *
 * There is no bulk endpoint, so Save issues one versioned update per changed or new row.
 * Adding works through the same update call: `update_fixed_cost_head_v2` upserts, and a
 * previously unseen cost_head_code is created as a custom head.
 *
 * Removals are staged the same way: the bin marks a row, and Save closes those heads
 * server-side. Nothing is written until Save, so a mis-click is undone with Cancel.
 * A reason is per line item and mandatory only where something is actually changing:
 * re-valuing or removing an existing head. Adding one needs none — it states what a cost
 * is rather than changing what the project already reported, and the edit log skips first
 * entries for the same reason. One reason shared across the save would have stamped the
 * same text on every head touched, which is what the log has to tell apart.
 */
export default function FixedCostsPanel({
	projectId,
	fixedCosts,
	loading,
	canEdit,
	canDelete,
	costFrozen,
	isCostPlus,
	onSaved,
}) {
	const rows = useMemo(() => fixedCosts || [], [fixedCosts]);

	const dispatch = useDispatch();
	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [draft, setDraft] = useState([]);
	const [selectedHead, setSelectedHead] = useState(undefined);
	const [customHead, setCustomHead] = useState('');
	const [newValue, setNewValue] = useState('');
	const [newCostToCustomer, setNewCostToCustomer] = useState('');

	const addingCustom = selectedHead === ADD_COST_HEAD_OPTION;

	// Reset the draft whenever the underlying rows change or edit mode opens
	useEffect(() => {
		setDraft(
			rows.map((r) => ({
				cost_head_code: r.cost_head_code,
				cost_head_name: r.cost_head_name,
				value: r.value ?? '',
				original: r.value,
				cost_to_customer: r.cost_to_customer ?? '',
				originalCostToCustomer: r.cost_to_customer,
				// Per-row and always blank on open: a reason describes this edit, so it is
				// never carried over from a previous one.
				reason: '',
				isNew: false,
				removed: false,
			}))
		);
	}, [rows, editing]);
	const pendingChanges = draft.filter(isRowDirty);
	// A newly added head needs no reason; re-valuing or removing an existing one does.
	const needsReason = (row) => !row.isNew;
	const canSave =
		pendingChanges.length > 0 &&
		pendingChanges.every((r) => !needsReason(r) || (r.reason || '').trim());

	// Rows staged for removal stay visible (struck through) but leave the total, so the
	// figure in the header is what the project will actually cost after Save.
	const working = editing ? draft : rows;
	const total = working.reduce((sum, r) => (r.removed ? sum : sum + (Number(r.value) || 0)), 0);

	const resetAddForm = () => {
		setSelectedHead(undefined);
		setCustomHead('');
		setNewValue('');
		setNewCostToCustomer('');
	};

	const startEdit = () => {
		resetAddForm();
		setEditing(true);
	};

	const cancelEdit = () => {
		setEditing(false);
		resetAddForm();
	};

	const setRowValue = (code, value) =>
		setDraft((prev) => prev.map((r) => (r.cost_head_code === code ? { ...r, value } : r)));

	const setRowCostToCustomer = (code, cost_to_customer) =>
		setDraft((prev) =>
			prev.map((r) => (r.cost_head_code === code ? { ...r, cost_to_customer } : r))
		);

	const setRowReason = (code, reason) =>
		setDraft((prev) => prev.map((r) => (r.cost_head_code === code ? { ...r, reason } : r)));

	/**
	 * A row that was only ever staged (never saved) is dropped outright — there is
	 * nothing on the server to close. A saved row is flagged and removed on Save.
	 */
	const removeRow = (code) =>
		setDraft((prev) =>
			prev
				.filter((r) => !(r.cost_head_code === code && r.isNew))
				.map((r) => (r.cost_head_code === code ? { ...r, removed: true } : r))
		);

	const restoreRow = (code) =>
		setDraft((prev) =>
			prev.map((r) => (r.cost_head_code === code ? { ...r, removed: false } : r))
		);

	const handleAddLineItem = () => {
		const name = (addingCustom ? customHead : selectedHead || '').trim();
		const code = toCode(name);
		const error = addLineItemError({
			name,
			code,
			value: newValue,
			costToCustomer: isCostPlus ? newCostToCustomer : '',
			addingCustom,
			taken: draft.some((r) => r.cost_head_code === code),
		});
		if (error) {
			message.error(error);
			return;
		}

		setDraft((prev) => [
			...prev,
			{
				cost_head_code: code,
				cost_head_name: name,
				value: newValue,
				reason: '',
				original: null,
				cost_to_customer: isCostPlus ? newCostToCustomer : '',
				originalCostToCustomer: null,
				isNew: true,
			},
		]);
		resetAddForm();
	};

	const handleSave = async () => {
		const removals = pendingChanges.filter((r) => r.removed);
		const dirty = pendingChanges.filter((r) => !r.removed);

		if (dirty.length === 0 && removals.length === 0) {
			setEditing(false);
			return;
		}

		const error = validateFixedCostSave(removals, dirty);
		if (error) {
			message.error(error);
			return;
		}

		setSaving(true);
		try {
			// No bulk endpoint — each head is versioned individually, one call per row.
			// Removals run first so a head can be dropped and re-added in one save.
			await runSequentially(removals, (row) =>
				dispatch(
					deleteWarehouseFixedCostV2({
						project_id: projectId,
						cost_head_code: row.cost_head_code,
						reason: (row.reason || '').trim(),
					})
				)
			);
			await runSequentially(dirty, (row) =>
				dispatch(
					updateWarehouseFixedCostV2({
						req_data: {
							project_id: projectId,
							cost_head_code: row.cost_head_code,
							cost_head_name: row.cost_head_name,
							value: Number(row.value),
							// Omitted for a new head — the API only requires it on an edit
							...((row.reason || '').trim() ? { reason: row.reason.trim() } : {}),
							// Omitted when blank so a per_order_slab project (or an unsplit
							// cost_plus head) never sends the field at all.
							...(row.cost_to_customer !== ''
								? { cost_to_customer: Number(row.cost_to_customer) }
								: {}),
						},
					})
				)
			);
			setEditing(false);
			onSaved();
		} catch (saveError) {
			// Errors surface through the API interceptor; stay in edit mode so the
			// operator keeps the values they typed
		} finally {
			setSaving(false);
		}
	};

	const columns = buildColumns({
		editing,
		canDelete,
		isCostPlus,
		setRowValue,
		setRowCostToCustomer,
		setRowReason,
		removeRow,
		restoreRow,
	});

	// Cost Plus only.
	const totalCostToCustomer = sumCostToCustomer(working);
	const totalGeneratedRevenue = totalCostToCustomer - total;

	const renderSummary = () => (
		<Table.Summary fixed>
			<Table.Summary.Row>
				{summaryCells({
					editing,
					isCostPlus,
					total,
					totalCostToCustomer,
					totalGeneratedRevenue,
				}).map((content, index) => (
					<Table.Summary.Cell
						// eslint-disable-next-line react/no-array-index-key -- cells are positional, no stable id
						key={index}
						index={index}
						align={index === 0 || editing ? 'left' : 'right'}
					>
						{content}
					</Table.Summary.Cell>
				))}
			</Table.Summary.Row>
		</Table.Summary>
	);

	// Shortlist minus anything already listed, then the custom escape hatch
	const headOptions = useMemo(() => {
		const taken = new Set(draft.map((r) => (r.cost_head_name || '').toLowerCase()));
		return [
			...COST_HEAD_OPTIONS.filter((name) => !taken.has(name.toLowerCase())).map((name) => ({
				value: name,
				label: name,
			})),
			{ value: ADD_COST_HEAD_OPTION, label: '+ Add custom cost head' },
		];
	}, [draft]);

	// Adding a head states what a cost is, so it needs no justification — only a later
	// change to that value does.
	const canAddLineItem =
		Boolean((addingCustom ? customHead : selectedHead || '').trim()) &&
		newValue !== '' &&
		!Number.isNaN(Number(newValue));

	const showEmpty = !loading && working.length === 0;

	return (
		<Panel>
			<PanelHeader>
				<Box>
					<PanelTitle>Fixed Costs</PanelTitle>
					<PanelSubtitle>
						Monthly recurring cost heads — {editing ? 'editing' : 'locked'}
					</PanelSubtitle>
				</Box>

				<Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
					<Box style={{ fontSize: 13, color: '#18405d', fontWeight: 600 }}>
						Total Cost to Company: {money(total)}
						<span style={{ margin: '0 8px' }}>|</span>
						Total Cost to Customer: {money(totalCostToCustomer)}
					</Box>
					{canEdit && (
						<FixedCostsHeaderActions
							editing={editing}
							saving={saving}
							canSave={canSave}
							costFrozen={costFrozen}
							onCancel={cancelEdit}
							onSave={handleSave}
							onStartEdit={startEdit}
						/>
					)}
				</Box>
			</PanelHeader>

			{showEmpty && !editing ? (
				<EmptyState>
					<div className="empty-title">No fixed cost heads yet</div>
					<div className="empty-body">
						Use Edit to add rent, on-roll manpower and other recurring costs.
					</div>
				</EmptyState>
			) : (
				<AntdTable
					rowKey="cost_head_code"
					size="small"
					columns={columns}
					dataSource={working}
					loading={loading}
					pagination={false}
					scroll={{ x: isCostPlus ? 780 : 420 }}
					// antd's summary API takes a render function by design
					// eslint-disable-next-line react/no-unstable-nested-components
					summary={renderSummary}
				/>
			)}

			{editing && (
				<Box mt="16px">
					<CostHeadAddRow custom={addingCustom ? 1 : 0}>
						<QuickAddField>
							<span className="field-label">Cost Head</span>
							<Select
								value={selectedHead}
								onChange={(v) => {
									setSelectedHead(v);
									if (v !== ADD_COST_HEAD_OPTION) setCustomHead('');
								}}
								placeholder="Select cost head"
								options={headOptions}
								showSearch
								optionFilterProp="label"
							/>
						</QuickAddField>

						{addingCustom && (
							<QuickAddField>
								<span className="field-label">New Cost Head</span>
								<Input
									placeholder="e.g. Water Charges"
									value={customHead}
									onChange={(e) => setCustomHead(e.target.value)}
									onPressEnter={handleAddLineItem}
								/>
							</QuickAddField>
						)}

						<QuickAddField>
							<span className="field-label">Cost to Company (₹)</span>
							<Input
								type="number"
								min={0}
								placeholder="0"
								value={newValue}
								onChange={(e) => setNewValue(e.target.value)}
								onPressEnter={handleAddLineItem}
							/>
						</QuickAddField>

						{isCostPlus && (
							<QuickAddField>
								<span className="field-label">Cost to Customer (₹)</span>
								<Input
									type="number"
									min={0}
									placeholder="Same as Value"
									value={newCostToCustomer}
									onChange={(e) => setNewCostToCustomer(e.target.value)}
									onPressEnter={handleAddLineItem}
								/>
							</QuickAddField>
						)}

						<QuickAddField>
							<Button
								type="primary"
								icon={<PlusOutlined />}
								disabled={!canAddLineItem}
								onClick={handleAddLineItem}
							>
								Add Line Item
							</Button>
						</QuickAddField>
					</CostHeadAddRow>

					<AddRowNote>
						Rows are written when you press Save. Removed heads stop applying from
						today; earlier months keep their recorded cost.
					</AddRowNote>
				</Box>
			)}
		</Panel>
	);
}

FixedCostsPanel.propTypes = {
	projectId: PropTypes.number.isRequired,
	fixedCosts: PropTypes.array,
	loading: PropTypes.bool,
	canEdit: PropTypes.bool,
	canDelete: PropTypes.bool,
	costFrozen: PropTypes.bool,
	isCostPlus: PropTypes.bool,
	onSaved: PropTypes.func.isRequired,
};

FixedCostsPanel.defaultProps = {
	fixedCosts: [],
	loading: false,
	canEdit: false,
	canDelete: false,
	costFrozen: false,
	isCostPlus: false,
};
